import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";

import api from "../../../services/api";
import { endpoints } from "../../../services/endpoints";

export default function PadlockEditAdmin() {
  const navigate = useNavigate();
  const { serialNumber } = useParams();

  const [padlockId, setPadlockId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const [form, setForm] = useState({
    name: "",
    serialNumber: "",
    companyId: null,
    assignedUserId: null,
    status: "ACTIVE",
  });

  const [companies, setCompanies] = useState([]); // [{value,label}]
  const [users, setUsers] = useState([]);         // [{value,label,companyId}]

  // --- 1) Resolver el ID a partir del serialNumber ---
  useEffect(() => {
    let alive = true;
    async function resolveId() {
      setResolving(true);
      setErr(null);
      try {
        // 1.a) Intentar con query param si el back lo soporta
        try {
          const r = await api.get(endpoints.padlock.list, { params: { serialNumber } });
          const list = r.data?.data ?? r.data;
          if (alive && Array.isArray(list)) {
            const found = list.find(
              (x) => String(x?.serialNumber ?? "").trim() === String(serialNumber ?? "").trim()
            );
            if (found?.id) { setPadlockId(found.id); return; }
          }
        } catch (_) {}

        // 1.b) Fallback: listar y filtrar en front
        const rAll = await api.get(endpoints.padlock.list);
        const arr = rAll.data?.data ?? rAll.data;
        if (alive && Array.isArray(arr)) {
          const found = arr.find(
            (x) => String(x?.serialNumber ?? "").trim() === String(serialNumber ?? "").trim()
          );
          if (found?.id) { setPadlockId(found.id); return; }
        }

        if (alive) setErr(new Error("No se pudo resolver el candado por serialNumber."));
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setResolving(false);
      }
    }

    if (serialNumber) resolveId();
    else { setErr(new Error("Falta el serialNumber en la URL.")); setResolving(false); }

    return () => { alive = false; };
  }, [serialNumber]);

  // --- 2) Cargar detalle + maestros usando el ID resuelto ---
  useEffect(() => {
    let alive = true;
    async function loadInitial() {
      if (!padlockId) return;
      setLoading(true);
      setErr(null);
      try {
        const [pRes, cRes, uRes] = await Promise.all([
          api.get(endpoints.padlock.byId(padlockId)),
          api.get(endpoints.companies.list),
          api.get(endpoints.users.list),
        ]);

        const p = pRes.data?.data ?? pRes.data;

        // Deducir assignedUserId a partir de permissions (si existe)
        let assignedUserId = null;
        if (Array.isArray(p?.permissions) && p.permissions.length > 0) {
          const perm = p.permissions.find((x) => x.canOpen) || p.permissions[0];
          assignedUserId = perm?.userId ?? null;
        }

        if (alive) {
          setForm({
            name: p?.name || "",
            serialNumber: p?.serialNumber || serialNumber || "",
            companyId: p?.company?.id ?? p?.companyId ?? null,
            assignedUserId,
            status: p?.status || "ACTIVE",
          });

          const companiesFmt = (cRes.data?.data ?? cRes.data ?? []).map((c) => ({
            value: c.id,
            label: c.name,
          }));
          setCompanies(companiesFmt);

          const usersFmt = (uRes.data?.data ?? uRes.data ?? []).map((u) => ({
            value: u.id,
            label: `${u.name ?? "—"} / ${u.identification ?? u.dni ?? ""} / ${u.company?.name ?? ""}`,
            companyId: u.companyId ?? u.company?.id ?? null,
          }));
          setUsers(usersFmt);
        }
      } catch (e) {
        console.error(e);
        setErr(e);
        alert("No se pudo cargar el candado/maestros");
        navigate("/locksadmin");
      } finally {
        if (alive) setLoading(false);
      }
    }
    loadInitial();
    return () => { alive = false; };
  }, [padlockId, navigate, serialNumber]);

  // selects
  const selectedCompany = useMemo(
    () => companies.find((c) => c.value === form.companyId) || null,
    [companies, form.companyId]
  );

  const selectedUser = useMemo(
    () => users.find((u) => u.value === form.assignedUserId) || null,
    [users, form.assignedUserId]
  );

  // Handlers
  const onInput = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSelectCompany = (opt) => {
    setForm((f) => ({
      ...f,
      companyId: opt ? opt.value : null,
      // si cambia de empresa y el usuario seleccionado no pertenece, lo limpiamos
      assignedUserId:
        opt && selectedUser && selectedUser.companyId !== opt.value
          ? null
          : f.assignedUserId,
    }));
  };

  const onSelectUser = (opt) => {
    setForm((f) => ({ ...f, assignedUserId: opt ? opt.value : null }));
  };

  const toggleStatus = async () => {
    if (!padlockId) return;
    try {
      const newStatus = form.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      await api.patch(endpoints.padlock.updateStatus(padlockId), { status: newStatus });
      setForm((f) => ({ ...f, status: newStatus }));
    } catch (err) {
      console.error(err);
      alert("No se pudo cambiar el estado");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!padlockId) return;
    try {
      setSaving(true);
      await api.patch(endpoints.padlock.byId(padlockId), {
        name: form.name,
        serialNumber: form.serialNumber, // ojo: si cambias el serial, tu URL actual ya no coincide
        companyId: form.companyId,
        assignedUserId: form.assignedUserId,
      });
      // Si permites cambiar el serial, podrías redirigir al nuevo detalle por serial:
      // navigate(`/locksadmin/${encodeURIComponent(form.serialNumber)}`);
      navigate("/locksadmin");
    } catch (err) {
      console.error(err);
      alert("No se pudo guardar el candado");
    } finally {
      setSaving(false);
    }
  };

  if (resolving || loading) return <div className="p-6">Cargando...</div>;
  if (err) return <div className="p-6 text-red-600">Error: {String(err)}</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-center mb-6">
        Editar candado (serial: {serialNumber})
      </h1>

      <form
        onSubmit={onSubmit}
        className="bg-white p-6 rounded shadow space-y-5 max-w-lg mx-auto"
      >
        {/* Estado + Toggle */}
        <div className="flex items-center justify-between border rounded p-3">
          <div className="text-sm">
            <div className="font-medium">Estado</div>
            {form.status === "ACTIVE" ? (
              <span className="text-green-600">Activo</span>
            ) : (
              <span className="text-red-600">Inactivo</span>
            )}
          </div>
          <button
            type="button"
            onClick={toggleStatus}
            className={`px-3 py-2 rounded border ${
              form.status === "ACTIVE"
                ? "text-red-600 hover:bg-red-50"
                : "text-green-600 hover:bg-green-50"
            }`}
            disabled={!padlockId}
          >
            {form.status === "ACTIVE" ? "Inactivar" : "Activar"}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block font-medium">Nombre</label>
            <input
              name="name"
              value={form.name}
              onChange={onInput}
              className="border rounded w-full p-2"
              required
            />
          </div>

          <div>
            <label className="block font-medium">Serial Number</label>
            <input
              name="serialNumber"
              value={form.serialNumber}
              onChange={onInput}
              className="border rounded w-full p-2"
              required
            />
            {/* Nota: si cambias el serial, quizá quieras actualizar la URL tras guardar */}
          </div>

          <div>
            <label className="block font-medium">Compañía</label>
            <Select
              options={companies}
              value={selectedCompany}
              onChange={onSelectCompany}
              isClearable
              placeholder="Selecciona compañía"
            />
          </div>

          <div>
            <label className="block font-medium">Usuario asignado</label>
            <Select
              options={
                form.companyId
                  ? users.filter((u) => u.companyId === form.companyId)
                  : users
              }
              value={selectedUser}
              onChange={onSelectUser}
              isClearable
              isSearchable
              placeholder="Selecciona usuario (opcional)"
            />
          </div>
        </div>

        {/* Botones abajo */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded border"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || !padlockId}
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}